import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { screenShareAPI } from '@/lib/api';
import { ScreenSharePeer } from '@/lib/screenShare';
import { BrutalCard, BrutalButton, BrutalInput } from '@/components/brutal';
import {
  Video, Monitor, Copy, Phone, PhoneOff, Loader2, MousePointer,
  Keyboard, Eye, Maximize2, Minimize2, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  idle: 'Ready',
  connecting: 'Connecting...',
  waiting: 'Waiting for user...',
  'peer-joined': 'User joined! Establishing connection...',
  connected: 'Connected — You have control',
  'peer-left': 'User disconnected',
  disconnected: 'Disconnected',
  error: 'Connection error',
  'connection-failed': 'WebRTC connection failed',
};

const STATUS_COLORS = {
  idle: '#94A3B8',
  connecting: '#F59E0B',
  waiting: '#F59E0B',
  'peer-joined': '#38BDF8',
  connected: '#10B981',
  'peer-left': '#EF4444',
  disconnected: '#94A3B8',
  error: '#EF4444',
  'connection-failed': '#EF4444',
};

const RemoteControlViewer = () => {
  const [status, setStatus] = useState('idle');
  const [activeSession, setActiveSession] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [controlEnabled, setControlEnabled] = useState(true);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const peerRef = useRef(null);

  // Fetch recent sessions
  const { data: sessions = [], refetch: refetchSessions } = useQuery({
    queryKey: ['screen-share-sessions'],
    queryFn: async () => (await screenShareAPI.listSessions()).data,
  });

  const createMut = useMutation({
    mutationFn: async () => (await screenShareAPI.createSession()).data,
    onSuccess: (data) => {
      setActiveSession(data);
      refetchSessions();
      toast.success(`Session created! Code: ${data.code}`);
      connectToSession(data.session_id);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create session'),
  });

  const endMut = useMutation({
    mutationFn: (sessionId) => screenShareAPI.endSession(sessionId),
    onSuccess: () => {
      disconnectSession();
      refetchSessions();
      toast.success('Session ended');
    },
  });

  const connectToSession = useCallback((sessionId) => {
    // Clean up any existing peer
    if (peerRef.current) {
      peerRef.current.disconnect();
    }

    const peer = new ScreenSharePeer({
      sessionId,
      role: 'admin',
      onRemoteStream: (stream) => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      },
      onStatus: (s) => {
        setStatus(s);
      },
    });

    peerRef.current = peer;
    peer.connect();
  }, []);

  const disconnectSession = useCallback(() => {
    if (peerRef.current) {
      peerRef.current.disconnect();
      peerRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStatus('idle');
    setActiveSession(null);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (peerRef.current) {
        peerRef.current.disconnect();
      }
    };
  }, []);

  // ── Control event handlers ──────────────────────────────────────────
  const getRelativeCoords = useCallback((e) => {
    if (!videoRef.current) return null;
    const rect = videoRef.current.getBoundingClientRect();
    const video = videoRef.current;
    // Account for video aspect ratio letterboxing
    const videoRatio = video.videoWidth / video.videoHeight;
    const elemRatio = rect.width / rect.height;
    let renderW, renderH, offsetX, offsetY;
    if (elemRatio > videoRatio) {
      renderH = rect.height;
      renderW = rect.height * videoRatio;
      offsetX = (rect.width - renderW) / 2;
      offsetY = 0;
    } else {
      renderW = rect.width;
      renderH = rect.width / videoRatio;
      offsetX = 0;
      offsetY = (rect.height - renderH) / 2;
    }
    const x = (e.clientX - rect.left - offsetX) / renderW;
    const y = (e.clientY - rect.top - offsetY) / renderH;
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return { x, y };
  }, []);

  const handleMouseEvent = useCallback((e, eventType) => {
    if (!controlEnabled || !peerRef.current) return;
    const coords = getRelativeCoords(e);
    if (!coords) return;
    peerRef.current.sendControlEvent({
      type: eventType,
      x: coords.x,
      y: coords.y,
      button: e.button,
    });
  }, [controlEnabled, getRelativeCoords]);

  const handleMouseMove = useCallback((e) => {
    if (!controlEnabled || !peerRef.current) return;
    const coords = getRelativeCoords(e);
    if (!coords) return;
    // Throttle cursor moves
    peerRef.current.sendCursorMove(coords.x, coords.y);
  }, [controlEnabled, getRelativeCoords]);

  const handleKeyEvent = useCallback((e, eventType) => {
    if (!controlEnabled || !peerRef.current) return;
    e.preventDefault();
    peerRef.current.sendControlEvent({
      type: eventType,
      key: e.key,
      code: e.code,
      ctrlKey: e.ctrlKey,
      shiftKey: e.shiftKey,
      altKey: e.altKey,
      metaKey: e.metaKey,
    });
  }, [controlEnabled]);

  const handleScroll = useCallback((e) => {
    if (!controlEnabled || !peerRef.current) return;
    const coords = getRelativeCoords(e);
    if (!coords) return;
    peerRef.current.sendControlEvent({
      type: 'scroll',
      x: coords.x,
      y: coords.y,
      deltaX: e.deltaX,
      deltaY: e.deltaY,
    });
  }, [controlEnabled, getRelativeCoords]);

  const toggleFullscreen = () => {
    if (!fullscreen && containerRef.current) {
      containerRef.current.requestFullscreen?.();
      setFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setFullscreen(false);
    }
  };

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const isConnected = status === 'connected';
  const isActive = activeSession && status !== 'idle' && status !== 'disconnected';

  return (
    <div className="space-y-6" data-testid="remote-control-viewer">
      {/* Header */}
      <BrutalCard shadow="xl" className="bg-gradient-to-r from-slate-800 to-slate-900 text-white border-indigo-500">
        <div className="flex items-center gap-3 mb-2">
          <Monitor size={24} className="text-indigo-400" />
          <h3 className="text-xl font-black uppercase">Remote Screen Control</h3>
        </div>
        <p className="text-sm text-slate-400">
          View and control a user's browser remotely. Create a session, share the code, and take control once they connect.
        </p>
      </BrutalCard>

      {/* Create / Active Session */}
      <BrutalCard shadow="lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-black text-lg uppercase">Session</h4>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-3 h-3 rounded-full" style={{ background: STATUS_COLORS[status] || '#94A3B8' }} />
              <span className="text-sm font-bold" style={{ color: STATUS_COLORS[status] || '#94A3B8' }}>
                {STATUS_LABELS[status] || status}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            {!isActive ? (
              <BrutalButton variant="indigo" size="lg" onClick={() => createMut.mutate()} disabled={createMut.isPending}
                data-testid="create-session-btn" className="flex items-center gap-2">
                <Video size={18} /> {createMut.isPending ? 'Creating...' : 'New Session'}
              </BrutalButton>
            ) : (
              <BrutalButton variant="danger" size="lg" onClick={() => endMut.mutate(activeSession.session_id)}
                disabled={endMut.isPending} data-testid="end-session-btn" className="flex items-center gap-2">
                <PhoneOff size={18} /> End Session
              </BrutalButton>
            )}
          </div>
        </div>

        {/* Session code + share link display */}
        {activeSession && (
          <div className="p-4 bg-indigo-50 border-4 border-black space-y-3" data-testid="session-info">
            <p className="font-black text-sm uppercase text-indigo-800">Share This Session</p>

            {/* Shareable URL — send this and the recipient lands on a page
                that auto-launches the screen-share prompt. */}
            {(() => {
              const shareUrl = `${window.location.origin}/share/${activeSession.code}`;
              return (
                <div className="space-y-1">
                  <p className="text-[10px] font-bold uppercase text-indigo-700">Direct Link (recommended)</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-3 py-2 bg-white border-4 border-black font-mono text-sm break-all select-all" data-testid="share-link-value">
                      {shareUrl}
                    </div>
                    <BrutalButton
                      variant="indigo"
                      size="sm"
                      onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Share link copied!'); }}
                      data-testid="copy-share-link-btn"
                    >
                      <Copy size={16} /> Copy link
                    </BrutalButton>
                  </div>
                  <p className="text-[11px] text-gray-600">
                    Text, email, or paste this into Slack. Whoever opens it will sign in (if needed) and the screen-share prompt fires automatically.
                  </p>
                </div>
              );
            })()}

            <div className="pt-2 border-t-2 border-indigo-200 space-y-1">
              <p className="text-[10px] font-bold uppercase text-indigo-700">Or read them this code</p>
              <div className="flex items-center gap-3">
                <div className="text-4xl font-black tracking-[0.3em] text-indigo-600 bg-white px-6 py-3 border-4 border-black select-all" data-testid="share-code-value">
                  {activeSession.code}
                </div>
                <BrutalButton variant="default" size="sm"
                  onClick={() => { navigator.clipboard.writeText(activeSession.code); toast.success('Code copied!'); }}>
                  <Copy size={16} />
                </BrutalButton>
              </div>
              <p className="text-[11px] text-gray-600">
                If they can&apos;t click the link, they can open the <strong>🛟 Support</strong> widget in their portal, hit <strong>Share Screen</strong>, and type this code.
              </p>
            </div>
          </div>
        )}
      </BrutalCard>

      {/* Remote screen viewer */}
      {isActive && (
        <BrutalCard shadow="lg" className="p-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-b-4 border-black">
            <div className="flex items-center gap-3">
              <Eye size={16} />
              <span className="font-bold text-sm">Remote View</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setControlEnabled(!controlEnabled)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold transition-all ${controlEnabled ? 'bg-green-100 text-green-700 border-2 border-green-400' : 'bg-gray-100 text-gray-500 border-2 border-gray-300'}`}
                data-testid="toggle-control">
                <MousePointer size={14} /> {controlEnabled ? 'Control ON' : 'Control OFF'}
              </button>
              <button onClick={toggleFullscreen}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold bg-slate-200 hover:bg-slate-300 transition-all">
                {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                {fullscreen ? 'Exit' : 'Fullscreen'}
              </button>
            </div>
          </div>

          <div
            ref={containerRef}
            className="relative bg-black"
            style={{ minHeight: 400 }}
            tabIndex={0}
            onKeyDown={(e) => handleKeyEvent(e, 'keydown')}
            onKeyUp={(e) => handleKeyEvent(e, 'keyup')}
          >
            {!isConnected && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-10">
                <Loader2 size={48} className="animate-spin mb-4 text-indigo-400" />
                <p className="font-bold text-lg">{STATUS_LABELS[status] || 'Connecting...'}</p>
                <p className="text-sm text-slate-400 mt-2">The user needs to share their screen using the code above</p>
              </div>
            )}

            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-auto max-h-[80vh]"
              style={{ cursor: controlEnabled && isConnected ? 'none' : 'default', objectFit: 'contain' }}
              onClick={(e) => handleMouseEvent(e, 'click')}
              onDoubleClick={(e) => handleMouseEvent(e, 'dblclick')}
              onMouseDown={(e) => handleMouseEvent(e, 'mousedown')}
              onMouseUp={(e) => handleMouseEvent(e, 'mouseup')}
              onMouseMove={handleMouseMove}
              onWheel={handleScroll}
              onContextMenu={(e) => { e.preventDefault(); handleMouseEvent(e, 'contextmenu'); }}
              data-testid="remote-screen-video"
            />

            {controlEnabled && isConnected && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-black/70 text-white text-xs font-bold">
                <MousePointer size={12} /> Click / type here to control &mdash; <Keyboard size={12} /> Keyboard active
              </div>
            )}
          </div>
        </BrutalCard>
      )}

      {/* Recent sessions */}
      <BrutalCard shadow="lg">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-black text-sm uppercase">Recent Sessions</h4>
          <button onClick={() => refetchSessions()} className="text-xs text-indigo-600 font-bold flex items-center gap-1">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        {sessions.length === 0 ? (
          <p className="text-sm text-gray-500">No sessions yet</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border-2 border-gray-200 bg-gray-50">
                <div>
                  <span className="font-bold text-sm tracking-wider">{s.code}</span>
                  <span className={`ml-3 text-xs font-bold px-2 py-0.5 rounded-full ${
                    s.status === 'connected' ? 'bg-green-100 text-green-700' :
                    s.status === 'waiting' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>{s.status}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{new Date(s.created_at).toLocaleString()}</span>
                  {s.status !== 'ended' && (
                    <>
                      <BrutalButton size="sm" variant="indigo" onClick={() => {
                        setActiveSession({ session_id: s.id, code: s.code });
                        connectToSession(s.id);
                      }}>
                        <Phone size={14} /> Rejoin
                      </BrutalButton>
                      <BrutalButton size="sm" variant="danger" onClick={() => endMut.mutate(s.id)}>
                        <PhoneOff size={14} />
                      </BrutalButton>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </BrutalCard>
    </div>
  );
};

export default RemoteControlViewer;
